import styled from "styled-components";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { rawColors } from "../../../design-system/tokens/colors";
import { borderRadius } from "../../../design-system/tokens/borders";
import { duration, easing } from "../../../design-system/tokens/transitions";

/** Modal heading — Figma uses Manrope SemiBold 16/28 for the title. */
export const HeaderTitle = styled.h2`
  margin: 0;
  ${typographyMixin("subtitle", 1)}
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** Form layout — column of field groups, 16px gap, no internal padding
 *  (the design-system ModalBody provides outer padding). */
export const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  width: 100%;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 100%;
`;

export const FieldLabel = styled.label`
  ${typographyMixin("caption", 3)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Incremental refresh row: title block on left, Switch on right. */
export const IncrementalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
  width: 100%;
`;

export const IncrementalText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const IncrementalTitle = styled.span`
  ${typographyMixin("body", 3)}
  color: ${({ theme }) => theme.colors.text.primary};
`;

/**
 * Per-Figma the helper text under "Incremental refresh" is 10px / 14px medium
 * — that's `overline-4` after our token alignment.
 */
export const IncrementalSubtitle = styled.span`
  ${typographyMixin("overline", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Helper / error caption rendered under a field. */
export const HelperText = styled.p<{ $error?: boolean }>`
  margin: 0;
  ${typographyMixin("caption", 4)}
  color: ${({ theme, $error }) =>
    $error ? rawColors.accent.red500 : theme.colors.text.secondary};
`;

/** Footer layout: Cancel pinned left, action buttons grouped on the right. */
export const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

/** Right-side group that holds Save schedule + Save and activate. */
export const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

/** Cancel button — body 3 typography, no fill, subtle border. */
export const CancelButton = styled.button`
  padding: ${spacing[2]} ${spacing[4]};
  border-radius: ${borderRadius.lg};
  border: 1px solid ${rawColors.stroke[200]};
  background-color: transparent;
  color: ${rawColors.text.mediumEmphasis};
  ${typographyMixin("body", 4)}
  font-weight: 500;
  cursor: pointer;
  transition: background-color ${duration.fast} ${easing.smooth};

  &:hover:not(:disabled) {
    background-color: ${rawColors.grey[50]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/** Yellow warning banner at the bottom of the schedule form. */
export const ActivationWarningBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  padding: ${spacing[3]};
  background-color: ${rawColors.accent.yellowLight};
  border-radius: ${borderRadius.md};
`;

export const ActivationWarningIcon = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: ${rawColors.accent.yellow500};

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

export const ActivationWarningText = styled.p`
  margin: 0;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.mediumEmphasis};
`;

/** Outlined primary button — white bg, primary-colored border + text. */
export const SaveScheduleButton = styled.button`
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borderRadius.lg};
  background-color: transparent;
  border: 1px solid ${rawColors.primary[500]};
  color: ${rawColors.primary[500]};
  ${typographyMixin("body", 4)}
  line-height: 1;
  font-weight: 500;
  cursor: pointer;
  transition: background-color ${duration.fast} ${easing.smooth};

  &:hover:not(:disabled) {
    background-color: ${rawColors.primary[50]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
